import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerListMatchingAttribute } from '~/types/api.graphql';
import { getMatchingAttributeLabel } from '~/utils/customerListMatchingRules';

import { AllOfLabel, AttributeLabel, ColumnLabel, RuleTitle as RuleTitleText } from './MatchingRulesField.styles';

interface RuleTitleProps {
  /** `[index, columnName, attribute]`; a ticked column may have no type yet. */
  fields: Array<[number, string, CustomerListMatchingAttribute | undefined]>;
  /** The simple view's section, whose columns are each a rule: "Any of a as Email, b as Phone". */
  anyOf?: boolean;
}

/**
 * A rule's columns, each with its type: "Phone_number as Phone" for one column, "All of ZIP as ZIP + User_Region as
 * State" for several.
 */
const RuleTitle = ({ fields, anyOf }: RuleTitleProps): JSX.Element => {
  const { t } = useTranslation();
  const label = anyOf
    ? t('aiInsights.a2ui.matchingRules.anyOf')
    : fields.length > 1 && t('aiInsights.a2ui.matchingRules.allOf');

  return (
    <RuleTitleText>
      {label && <AllOfLabel>{label}</AllOfLabel>}
      {fields.map(([index, columnName, attribute], fieldIndex) => (
        <React.Fragment key={index}>
          {fieldIndex > 0 && <AttributeLabel>{anyOf ? ', ' : ' + '}</AttributeLabel>}
          <ColumnLabel>{columnName}</ColumnLabel>
          {attribute && (
            <AttributeLabel>
              {' '}
              {t('aiInsights.a2ui.matchingRules.asAttribute', { attribute: getMatchingAttributeLabel(attribute) })}
            </AttributeLabel>
          )}
        </React.Fragment>
      ))}
    </RuleTitleText>
  );
};

export default RuleTitle;
